import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  ButtonGroup,
  useTheme,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  CalendarMonth as MonthIcon,
  CalendarViewWeek as WeekIcon,
  CalendarToday as DayIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';

const CalendarHeader = ({
  title,
  currentView,
  onViewChange,
  onPrevious,
  onNext,
  onToday,
  onNewEvent,
  isMobile = false,
  currentDate,
  onDateChange
}) => {
  const theme = useTheme();
  
  // State for dropdown menus
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [calendarDate, setCalendarDate] = useState(currentDate);
  const [showYearView, setShowYearView] = useState(false);
  
  // Helper functions for calendar widget
  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month  
    const lastDay = new Date(year, month + 1, 0);
    
    // First day of the calendar grid (might be from previous month)
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    startDate.setDate(firstDay.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Monday = 0
    
    // Generate 6 weeks of days (42 days)
    const days = [];
    const currentDay = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };
  
  const getYearMonths = () => {
    const year = calendarDate.getFullYear();
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 12; i++) {
      months.push({
        name: monthNames[i],
        date: new Date(year, i, 1)
      });
    }
    
    return months;
  };
  
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  const isCurrentMonth = (date) => {
    return date.getMonth() === calendarDate.getMonth();
  };
  
  const isSelectedDate = (date) => {
    return date.toDateString() === currentDate.toDateString();
  };
  
  // Helper function to get week start (Monday)
  const getWeekStart = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday
    start.setDate(diff);
    return start;
  };
  
  // Helper function to check if date is in selected week (for week view)
  const isInSelectedWeek = (date) => {
    // Always show week selection in week popup, regardless of current view
    if (currentView !== 'week') return false;
    
    const weekStart = getWeekStart(currentDate);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    weekStart.setHours(0, 0, 0, 0);
    weekEnd.setHours(23, 59, 59, 999);
    
    return checkDate >= weekStart && checkDate <= weekEnd;
  };
  
  // Helper function to get week position for styling
  const getWeekPosition = (date) => {
    if (currentView !== 'week' || !isInSelectedWeek(date)) return null;
    
    const weekStart = getWeekStart(currentDate);
    const daysDiff = Math.floor((date - weekStart) / (1000 * 60 * 60 * 24));
    
    return {
      isFirst: daysDiff === 0,
      isLast: daysDiff === 6,
      isMiddle: daysDiff > 0 && daysDiff < 6
    };
  };
  
  
  // Handle date selection for week view (select entire week)
  const handleWeekDateSelect = (date) => {
    const weekStart = getWeekStart(date);
    onDateChange(weekStart);
    handleDateMenuClose();
  };
  
  const handleDateMenuOpen = (event) => {
    setDateMenuAnchor(event.currentTarget);
    // For week view, ensure we start with the week aligned properly
    if (currentView === 'week') {
      const weekStart = getWeekStart(currentDate);
      setCalendarDate(weekStart);
    } else {
      setCalendarDate(currentDate);
    }
    setShowYearView(false);
  };
  
  const handleDateMenuClose = () => {
    setDateMenuAnchor(null);
    setShowYearView(false);
  };
  
  const handleDateSelect = (date) => {
    onDateChange(date);
    handleDateMenuClose();
  };
  
  const handleCalendarPrevious = () => {
    if (showYearView) {
      setCalendarDate(new Date(calendarDate.getFullYear() - 1, calendarDate.getMonth(), 1));
    } else {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
    }
  };
  
  const handleCalendarNext = () => {
    if (showYearView) {
      setCalendarDate(new Date(calendarDate.getFullYear() + 1, calendarDate.getMonth(), 1));
    } else {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
    }
  };
  
  const handleTodaySelect = () => {
    const today = new Date();
    onDateChange(today);
    handleDateMenuClose();
  };
  
  const handleMonthSelect = (monthDate) => {
    setCalendarDate(monthDate);
    setShowYearView(false);
  };
  
  const toggleYearView = () => {
    setShowYearView(!showYearView);
  };

  const viewOptions = [
    { value: 'month', label: 'Month', icon: <MonthIcon /> },
    { value: 'week', label: 'Week', icon: <WeekIcon /> },
    { value: 'day', label: 'Day', icon: <DayIcon /> }
  ];

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 2.5 },
      bgcolor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      gap: { xs: 1, sm: 1.5, md: 2 }
    }}>
      {/* Left section - Navigation */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 0.5, sm: 1, md: 2 },
        flex: { xs: 1, sm: 1 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        justifyContent: { xs: 'space-between', sm: 'flex-start' }
      }}>
        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
          <IconButton 
            onClick={onPrevious}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              minWidth: { xs: 36, md: 40 },
              height: { xs: 36, md: 40 },
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50'
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          
          <Button
            onClick={onToday}
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={!isMobile ? <TodayIcon /> : undefined}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 70, md: 100 },
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              px: { xs: 1.5, md: 2 },
              '&:hover': {
                bgcolor: 'primary.50'
              }
            }}
          >
            Today
          </Button>
          
          <IconButton 
            onClick={onNext}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              minWidth: { xs: 36, md: 40 },
              height: { xs: 36, md: 40 },
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50'
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* Date/Period Selector */}
        <Button
          onClick={handleDateMenuOpen}
          variant="text"
          endIcon={<ArrowDropDownIcon />}
          sx={{
            ml: { xs: 0.5, md: 2 },
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: { xs: '0.8rem', sm: '1rem', md: '1.25rem' },
            color: 'text.primary',
            minWidth: 'auto',
            px: { xs: 0.5, sm: 1, md: 2 },
            maxWidth: { xs: '200px', sm: 'none' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
        >
          {title}
        </Button>
      </Box>

      {/* Right section - View switcher and actions */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 0.5, sm: 1, md: 2 },
        justifyContent: { xs: 'center', sm: 'flex-end' },
        flex: { xs: 'none', sm: 'none' },
        flexWrap: { xs: 'nowrap', sm: 'nowrap' }
      }}>
        {/* View switcher */}
        <ButtonGroup 
          variant="outlined" 
          size={isMobile ? 'small' : 'medium'}
          sx={{
            '& .MuiButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              px: { xs: 0.5, sm: 1.5, md: 2 },
              minWidth: { xs: 32, sm: 50, md: 60 },
              fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.875rem' },
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50'
              },
              '&.Mui-selected, &.MuiButton-contained': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderColor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  borderColor: 'primary.dark'
                }
              }
            }
          }}
        >
          {viewOptions.map((option) => (
            <Button
              key={option.value}
              onClick={() => onViewChange(option.value)}
              variant={currentView === option.value ? 'contained' : 'outlined'}
              startIcon={!isMobile ? option.icon : null}
              className={currentView === option.value ? 'Mui-selected' : ''}
            >
              {isMobile ? option.value.charAt(0).toUpperCase() : option.label}
            </Button>
          ))}
        </ButtonGroup>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* New Event button */}
          {onNewEvent && (
            <Button
              onClick={onNewEvent}
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<AddIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: isMobile ? 2 : 3,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 2
                }
              }}
            >
              {isMobile ? 'New' : 'New Event'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Teams-style Date Picker Widget */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={handleDateMenuClose}
        PaperProps={{
          sx: {
            width: { 
              xs: '95vw',
              sm: currentView === 'month' ? '300px' : '380px'
            },
            maxWidth: { xs: '95vw', sm: '450px' },
            maxHeight: { xs: '70vh', sm: '80vh' },
            mt: { xs: 1, sm: 1 },
            p: 0,
            borderRadius: { xs: '12px', sm: '8px' },
            overflow: 'auto',
            boxSizing: 'border-box',
            // Better mobile shadows
            boxShadow: { 
              xs: '0 8px 32px rgba(0,0,0,0.12)', 
              sm: '0 4px 16px rgba(0,0,0,0.1)' 
            }
          }
        }}
        transformOrigin={{ 
          horizontal: 'left', 
          vertical: 'top' 
        }}
        anchorOrigin={{ 
          horizontal: 'left', 
          vertical: 'bottom' 
        }}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
      >
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          
          {currentView === 'month' ? (
            // Month view: Only show year and month selection (no daily calendar)
            <>
              {/* Year Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1.5
              }}>
                <IconButton 
                  size="small" 
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear() - 1, calendarDate.getMonth(), 1))}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {calendarDate.getFullYear()}
                </Typography>
                
                <IconButton 
                  size="small" 
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear() + 1, calendarDate.getMonth(), 1))}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Month Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(4, 1fr)' },
                gap: { xs: 0.75, sm: 1 },
                mb: 1.5,
                width: '100%',
                maxWidth: '100%'
              }}>
                {getYearMonths().map((month, index) => (
                  <Button
                    key={index}
                    onClick={() => {
                      const selectedDate = new Date(calendarDate.getFullYear(), month.date.getMonth(), 1);
                      onDateChange(selectedDate);
                      handleDateMenuClose();
                    }}
                    variant={month.date.getMonth() === currentDate.getMonth() && 
                            month.date.getFullYear() === currentDate.getFullYear() ? 'contained' : 'text'}
                    size="small"
                    sx={{
                      minWidth: 0,
                      width: '100%',
                      maxWidth: '100%',
                      py: { xs: 0.5, sm: 1 },
                      px: { xs: 0.5, sm: 1 },
                      textTransform: 'none',
                      fontSize: { xs: '0.8rem', sm: '0.8rem' },
                      bgcolor: month.date.getMonth() === currentDate.getMonth() && 
                              month.date.getFullYear() === currentDate.getFullYear() ? 'primary.main' : 'transparent',
                      color: month.date.getMonth() === currentDate.getMonth() && 
                             month.date.getFullYear() === currentDate.getFullYear() ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        bgcolor: month.date.getMonth() === currentDate.getMonth() && 
                                 month.date.getFullYear() === currentDate.getFullYear() ? 'primary.dark' : 'action.hover'
                      }
                    }}
                  >
                    {month.name}
                  </Button>
                ))}
              </Box>
            </>
          ) : currentView === 'week' ? (
            // Week view: Show calendar with week selection (no year panel)
            <>
              {/* Month Calendar Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1.5
              }}>
                <IconButton 
                  size="small" 
                  onClick={handleCalendarPrevious}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {calendarDate.toLocaleDateString('en-GB', { 
                    month: 'long',
                    year: 'numeric'
                  })}
                </Typography>
                
                <IconButton 
                  size="small" 
                  onClick={handleCalendarNext}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Week Day Headers */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: { xs: 0.25, sm: 0.5 },
                mb: 1
              }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      py: 0.5
                    }}
                  >
                    {day}
                  </Typography>
                ))}
              </Box>

              {/* Calendar Grid with Week Selection */}
              <Box sx={{
                display: { xs: 'block', sm: 'grid' },
                gridTemplateColumns: { sm: 'repeat(7, 1fr)' },
                gap: 0, // No gap for seamless week selection
                mb: 1.5,
                width: '100%',
                overflow: 'visible'
              }}>
                {/* Mobile: Responsive weeks grid */}
                <Box sx={{
                  display: { xs: 'grid', sm: 'contents' },
                  gridTemplateColumns: { xs: 'repeat(7, minmax(0, 1fr))', sm: 'none' },
                  gridTemplateRows: { xs: 'repeat(6, 28px)', sm: 'none' },
                  gap: { xs: 0, sm: 0 },
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  pb: { xs: 1, sm: 0 }
                }}>
                {getCalendarDays(calendarDate).map((date, index) => {
                  const isCurrentMonthDate = isCurrentMonth(date);
                  const isTodayDate = isToday(date);
                  const isSelected = isSelectedDate(date);
                  const isInWeek = isInSelectedWeek(date);
                  const weekPos = getWeekPosition(date);

                  return (
                    <Button
                      key={index}
                      onClick={() => handleWeekDateSelect(date)}
                      variant="text"
                      size="small"
                      sx={{
                        width: '100%',
                        height: 28,
                        minWidth: 0,
                        maxWidth: '100%',
                        p: 0,
                        m: 0,
                        border: 'none',
                        // Seamless week range styling - natural approach
                        borderRadius: isInWeek 
                          ? weekPos?.isFirst 
                            ? '4px 0 0 4px'
                            : weekPos?.isLast 
                              ? '0 4px 4px 0'
                              : '0'
                          : isTodayDate 
                            ? '50%' 
                            : '4px',
                        fontSize: { xs: '0.8rem', sm: '0.8rem' },
                        color: isInWeek
                            ? 'primary.contrastText'
                            : !isCurrentMonthDate 
                              ? 'text.disabled' 
                              : isTodayDate 
                                ? 'primary.main'
                                : 'text.primary',
                        bgcolor: isInWeek
                          ? 'primary.main'
                          : isTodayDate 
                            ? 'primary.50'
                            : 'transparent',
                        fontWeight: isTodayDate || isInWeek ? 'bold' : 'normal',
                        zIndex: isTodayDate ? 3 : isInWeek ? 2 : 0,
                        position: 'relative',
                        // Remove any margin or padding that creates gaps
                        '&:hover': {
                          bgcolor: isInWeek
                            ? 'primary.dark'
                            : 'action.hover',
                          zIndex: 3
                        }
                      }}
                    >
                      {date.getDate()}
                    </Button>
                  );
                })}
                </Box>
              </Box>

              {/* Today Button */}
              <Button
                onClick={handleTodaySelect}
                variant="text"
                size="small"
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  color: 'primary.main'
                }}
              >
                Today
              </Button>
            </>
          ) : (
            // Day view: Show single calendar (same as week view but with individual date selection)
            <>
              {/* Month Calendar Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1.5
              }}>
                <IconButton 
                  size="small" 
                  onClick={handleCalendarPrevious}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {calendarDate.toLocaleDateString('en-GB', { 
                    month: 'long',
                    year: 'numeric'
                  })}
                </Typography>
                
                <IconButton 
                  size="small" 
                  onClick={handleCalendarNext}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Week Day Headers */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: { xs: 0.25, sm: 0.5 },
                mb: 1
              }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      py: 0.5
                    }}
                  >
                    {day}
                  </Typography>
                ))}
              </Box>

              {/* Calendar Grid for Day Selection */}
              <Box sx={{
                display: { xs: 'block', sm: 'grid' },
                gridTemplateColumns: { sm: 'repeat(7, 1fr)' },
                gap: { sm: 0.5 },
                mb: 1.5,
                width: '100%',
                // Mobile: no horizontal scroll, use proper grid
                overflowX: { xs: 'visible', sm: 'visible' },
                overflowY: 'visible'
              }}>
                {/* Mobile: Responsive day grid */}
                <Box sx={{
                  display: { xs: 'grid', sm: 'contents' },
                  gridTemplateColumns: { xs: 'repeat(7, minmax(0, 1fr))', sm: 'none' },
                  gridTemplateRows: { xs: 'repeat(6, 32px)', sm: 'none' },
                  gap: { xs: 0, sm: 0 },
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  pb: { xs: 1, sm: 0 }
                }}>
                {getCalendarDays(calendarDate).map((date, index) => {
                  const isCurrentMonthDate = isCurrentMonth(date);
                  const isTodayDate = isToday(date);
                  const isSelected = isSelectedDate(date);

                  return (
                    <Button
                      key={index}
                      onClick={() => handleDateSelect(date)}
                      variant="text"
                      size="small"
                      sx={{
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        maxWidth: 28,
                        p: 0,
                        borderRadius: '50%',
                        mx: 'auto',
                        fontSize: { xs: '0.8rem', sm: '0.8rem' },
                        color: !isCurrentMonthDate 
                          ? 'text.disabled' 
                          : isSelected 
                            ? 'primary.contrastText'
                            : isTodayDate 
                              ? 'primary.main'
                              : 'text.primary',
                        bgcolor: isSelected 
                          ? 'primary.main'
                          : isTodayDate 
                            ? 'primary.50'
                            : 'transparent',
                        fontWeight: isTodayDate || isSelected ? 'bold' : 'normal',
                        '&:hover': {
                          bgcolor: isSelected 
                            ? 'primary.dark'
                            : 'action.hover'
                        }
                      }}
                    >
                      {date.getDate()}
                    </Button>
                  );
                })}
                </Box>
              </Box>

              {/* Today Button */}
              <Button
                onClick={handleTodaySelect}
                variant="text"
                size="small"
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  color: 'primary.main'
                }}
              >
                Today
              </Button>
            </>
          )}
        </Box>
      </Menu>
    </Box>
  );
};

export default CalendarHeader;